import React, { ReactNode, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Result,
  Typography,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Store } from 'rc-field-form/es/interface';
import SimpleForm from 'App/lib/Form/SimpleForm';
import { AccountAction } from 'App/lib/Alliance/fetcher';
import PwaInstallPrompt from 'App/App/PwaInstallPrompt';
import ResponsiveProvider from 'App/lib/Hooks/ResponsiveContext';

interface LoginFormProps {
  onSuccess: () => void;
  forgotPasswordLink?: ReactNode;
}

const initialFormData: Store = {
  user: '',
  password: '',
  remember: true,
};

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  forgotPasswordLink,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onLogin = () => {
    setSuccess(true);
    localStorage.setItem('accountAction', AccountAction.LOG_IN);
    onSuccess();
  };

  if (success) {
    return <Result icon={<LoadingOutlined />} subTitle="Logging in.." />;
  }

  return (
    <div className="login">
      <ResponsiveProvider>
        <PwaInstallPrompt />
      </ResponsiveProvider>
      <div className="logo" />
      <SimpleForm
        url="/login"
        spinner={false}
        initialValues={initialFormData}
        onWorking={setIsLoading}
        onSuccess={onLogin}>
        <Form.Item name="username">
          <Input
            data-testid="username"
            size="large"
            disabled={isLoading}
            placeholder="Username/Access ID..."
            style={{
              fontSize: '1.1em',
              fontWeight: 900,
              letterSpacing: '1px',
              backgroundColor: 'rgba(0,0,0,.3)',
            }}
          />
        </Form.Item>

        <Form.Item name="password">
          <Input.Password
            type="password"
            data-testid="password"
            size="large"
            disabled={isLoading}
            placeholder="Password..."
            style={{
              fontSize: '1.1em',
              fontWeight: 900,
              letterSpacing: '1px',
              backgroundColor: 'rgba(0,0,0,.3)',
            }}
          />
        </Form.Item>

        <Form.Item>
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox
              data-testid="remember"
              disabled={isLoading}
              style={{
                fontSize: '1.1em',
                fontWeight: 900,
                letterSpacing: '1px',
              }}>
              Remember me
            </Checkbox>
          </Form.Item>
          {forgotPasswordLink}
        </Form.Item>

        <Form.Item className="form-actions">
          <Button
            loading={isLoading}
            block
            type="primary"
            htmlType="submit"
            data-testid="submit"
            size="large"
            style={{ width: '40%' }}>
            Log in
          </Button>
        </Form.Item>

        <p style={{ textAlign: 'center', margin: '2.5rem 0' }}>
          <Typography.Link
            underline
            href="mailto:livestock@alliance.co.nz?subject=Forgot%20password&body=Hi%2C%0A%0AI've%20forgotten%20my%20password%2C%20can%20you%20please%20reset%20it.%0A%0AUsername%3A%20PLEASE%20ENTER%20YOUR%20USERNAME">
            Forgot Password?
          </Typography.Link>
        </p>

        <Divider plain style={{ marginBottom: '2.5rem' }}>
          New to Alliance Farmers Produce?
        </Divider>

        <Form.Item className="form-actions">
          <Button
            block
            type="primary"
            href="mailto:livestock@alliance.co.nz?subject=New%20User%20Registration&body=Hi%2C%0A%0AI'd%20like%20to%20register%20for%20access%20to%20the%20supplier%20portal%0A%0A%0APlease%20complete%20the%20information%20below%0A%0A%0AClient%20number%3A%0A%0ATrading%20Name%3A%0A%0AAccount%20role%3A%0A%0AName%3A%0A%0AEmail%20address%3A%0A%0APhone%3A"
            size="large"
            style={{ width: '40%' }}>
            Register
          </Button>
        </Form.Item>
      </SimpleForm>
    </div>
  );
};

export default LoginForm;
